import React, { useState, useEffect, useContext } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import './pages.css';
import { Container, Carousel, Card, Row, Col, Button, Form, InputGroup, Alert } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { API } from '../config/api';
import getImageUrl from '../utils/getImageUrl';
import CartContext from '../context/CartContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faStar, faMotorcycle, faThumbsUp, faCrown, faBirthdayCake, faCookie, faBowlRice, faBreadSlice, faBasketShopping, faBowlFood, faAppleWhole, faGift } from '@fortawesome/free-solid-svg-icons';

const Home = () => {
  const navigate = useNavigate();
	const categories = [
		{ name: "Cakes", icon: <FontAwesomeIcon icon={faBirthdayCake} size="4x" className="text-danger" />, slug: "cake" },
		{ name: "Cookies", icon: <FontAwesomeIcon icon={faCookie} size="4x" className="text-warning" />, slug: "cookies" },
		{ name: "Rice Dishes", icon: <FontAwesomeIcon icon={faBowlRice} size="4x" className="text-success" />, slug: "rice" },
		{ name: "Breads", icon: <FontAwesomeIcon icon={faBreadSlice} size="4x" className="text-info" />, slug: "bread" },
		{ name: "Snacks", icon: <FontAwesomeIcon icon={faBasketShopping} size="4x" className="text-primary" />, slug: "snacks" },
		{ name: "Pasta", icon: <FontAwesomeIcon icon={faBowlFood} size="4x" className="text-secondary" />, slug: "pasta" },
		{ name: "Fruit salad", icon: <FontAwesomeIcon icon={faAppleWhole} size="4x" className="text-success" />, slug: "fruit salad" },
		{ name: "Custom Orders", icon: <FontAwesomeIcon icon={faGift} size="4x" className="text-danger" />, slug: "custom" },
	];

	const { addToCart } = useContext(CartContext);
	const [featuredItems, setFeaturedItems] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [currentSlide, setCurrentSlide] = useState(0);
	const [fetchError, setFetchError] = useState(null);

	const testimonials = [
		{
			id: 1,
			name: "Adeola Johnson",
			role: "Wedding Planner",
			comment: "Noureesh Foods made the most beautiful wedding cake! It was delicious and arrived right on time. Our guests couldn't stop talking about it!",
			rating: 5,
			image: "https://randomuser.me/api/portraits/women/44.jpg"
		},
		{
			id: 2,
			name: "Emeka Okoro",
			role: "Corporate Client",
			comment: "We order desserts for all our office events from Noureesh. Consistent quality and their biryani is the best in Lagos!",
			rating: 4,
			image: "https://randomuser.me/api/portraits/men/32.jpg"
		},
		{
			id: 3,
			name: "Chioma Adebayo",
			role: "Satisfied Customer",
			comment: "The chocolate cookies are addictive! I order every week for my family. Delivery is always faster than promised.",
			rating: 5,
			image: "https://randomuser.me/api/portraits/women/63.jpg"
		},
		{
			id: 4,
			name: "Tunde Okafor",
			role: "Event Organizer",
			comment: "Reliable service and exceptional taste. The cakes look exactly like the pictures - truly Instagram worthy!",
			rating: 5,
			image: "https://randomuser.me/api/portraits/men/54.jpg"
		}
	];

	const [email, setEmail] = useState('');
	const [subscribed, setSubscribed] = useState(false);
	const [error, setError] = useState(null);
	const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

	// Fetch featured products
  useEffect(() => {
    const fetchFeaturedProducts = async () => {
      try {
        setIsLoading(true);
				setFetchError(null);
				const response = await fetch(`${API}/products/featured`);
				if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setFeaturedItems(data);
      } catch (err) {
        console.error('Error fetching featured products:', err);
				setFetchError('Unable to load featured products. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchFeaturedProducts();
  }, []);

  // Auto-rotate carousel if more than 4 featured items
  useEffect(() => {
    if (featuredItems.length > 4) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % Math.ceil(featuredItems.length / 4));
      }, 200000);

      return () => clearInterval(interval);
    }
  }, [featuredItems.length]);

	// Update on window resize
	useEffect(() => {
		const handleResize = () => setIsMobile(window.innerWidth < 768);
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

  // Group featured items into chunks of 4 for desktop, single items for mobile
  const getFeaturedChunks = () => {
		const chunks = [];
		
		if (isMobile) {
			// For mobile: one item per slide
			for (let i = 0; i < featuredItems.length; i++) {
				chunks.push([featuredItems[i]]);
			}
		} else {
			// For desktop: 4 items per slide
			for (let i = 0; i < featuredItems.length; i += 4) {
				chunks.push(featuredItems.slice(i, i + 4));
			}
		}
		return chunks;
	};

  const featuredChunks = getFeaturedChunks();

	const handleSubmit = async (e) => {
		e.preventDefault();
		setError(null); // Reset error state
		
		// Basic validation
		if (!email || !email.includes('@')) {
			setError('Please enter a valid email address');
			return;
		}

		try {
			const response = await fetch(`${API}/newsletter/subscribe`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ email }),
			});

			// Check if response is JSON
			const contentType = response.headers.get('content-type');
			if (!contentType || !contentType.includes('application/json')) {
				const text = await response.text();
				throw new Error(text || 'Server error');
			}

			const data = await response.json();

			if (!response.ok) {
				throw new Error(data.message || 'Subscription failed');
			}

			if (data.alreadySubscribed) {
				setError('This email is already subscribed');
			} else {
				setSubscribed(true);
				setEmail('');
			}
		} catch (err) {
			console.error('Subscription error:', err);
			setError(err.message || 'Failed to subscribe. Please try again.');
		}

		// Clear messages after 3 seconds
		setTimeout(() => {
			setError(null);
			setSubscribed(false);
		}, 3000);
	};
	
  return (
		<div className="App">
		  {/* Hero Carousel */}
		  <Carousel className="hero-carousel">
				{/* Cake */}
				<Carousel.Item>
				  <img
						className="d-block w-100"
						src="/image/cup-cake.jpg"
						alt="Delicious Cakes"
				  />
				  <Carousel.Caption className="carousel-caption-custom">
						<h3>Delicious Cakes</h3>
						<p>Order our freshly baked cakes for any occasion</p>
						<Button variant="danger" onClick={() => navigate('/menu?category=cakes')}>Order Now</Button>
				  </Carousel.Caption>
				</Carousel.Item>
				{/* Pasta */}
				<Carousel.Item>
				  <img
						className="d-block w-100"
						src="/image/pasta.jpg"
						alt="Homemade Pasta"
				  />
				  <Carousel.Caption className="carousel-caption-custom">
						<h3>Homemade Pasta</h3>
						<p>Enjoy our rich and flavorful pasta dishes, made with love</p>
						<Button variant="danger" onClick={() => navigate('/menu?category=pasta')}>Order Now</Button>
				  </Carousel.Caption>
				</Carousel.Item>
				{/* Bread */}
				<Carousel.Item>
				  <img
						className="d-block w-100"
						src="/image/bread.jpg"
						alt="Freshly Baked Bread"
				  />
				  <Carousel.Caption className="carousel-caption-custom">
						<h3>Freshly Baked Bread</h3>
						<p>Experience the aroma and taste of our soft, homemade breads</p>
						<Button variant="danger" onClick={() => navigate('/menu?category=breads')}>Order Now</Button>
				  </Carousel.Caption>
				</Carousel.Item>
				{/* Rice Dishes */}
				<Carousel.Item>
				  <img
						className="d-block w-100"
						src="/image/rice.jpg"
						alt="Delicious Rice Dishes"
				  />
				  <Carousel.Caption className="carousel-caption-custom">
						<h3>Delicious Rice Dishes</h3>
						<p>Savor our aromatic and flavorful rice specialties, perfect for every meal</p>
						<Button variant="danger" onClick={() => navigate('/menu?category=rice')}>Order Now</Button>
				  </Carousel.Caption>
				</Carousel.Item>
		  </Carousel>
	  
			{/* Delivery Banner */}
			<Container fluid className="delivery-banner py-4 animate__animated animate__fadeIn">
				<Row className="g-0 justify-content-center text-center align-items-stretch">
					<Col xs={12} md={4} className="mb-3 mb-md-0 d-flex align-items-stretch">
						<div className="w-100 d-flex align-items-center justify-content-center hover-scale py-3">
							<FontAwesomeIcon 
								icon={faMotorcycle} 
								className="me-2 fs-4 icon-bounce" 
							/>
							<div>
								<h5 className="mb-0 fw-bold">BRB Fast Cake Delivery</h5>
								<small className="text-muted">in Lagos</small>
							</div>
						</div>
					</Col>
					<Col xs={12} md={4} className="mb-3 mb-md-0 d-flex align-items-stretch">
						<div className="w-100 d-flex align-items-center justify-content-center hover-scale py-3">
							<FontAwesomeIcon 
								icon={faClock} 
								className="me-2 fs-4 icon-pulse" 
							/>
							<div>
								<h5 className="mb-0 fw-bold">30-120 mins</h5>
								<small className="text-muted">Dispatch Time</small>
							</div>
						</div>
					</Col>
					<Col xs={12} md={4} className="d-flex align-items-stretch">
						<div className="w-100 d-flex align-items-center justify-content-center hover-scale py-3">
							<FontAwesomeIcon 
								icon={faThumbsUp} 
								className="me-2 fs-4 icon-shake" 
							/>
							<div>
								<h5 className="mb-0 fw-bold">100% Satisfaction</h5>
								<small className="text-muted">guaranteed</small>
							</div>
						</div>
					</Col>
				</Row>
			</Container>

			{/* Featured Items */}
      <Container className="my-5 bg-light py-4">
        <h2 className="text-center mb-4">
          <FontAwesomeIcon icon={faCrown} className="me-2 text-warning" />
          Featured Items
        </h2>
        
				{fetchError ? (
					<Alert variant="warning" className="text-center">
						{fetchError}
					</Alert>
        ) : isLoading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-danger" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : featuredItems.length === 0 ? (
          <div className="text-center py-4">
            <p>No featured items available</p>
          </div>
        ) : (
          <>
            {/* Carousel for more than 4 items */}
            {featuredItems.length > 4 ? (
              <Carousel 
                activeIndex={currentSlide} 
                onSelect={setCurrentSlide}
                indicators={false}
                controls={true}
                interval={10000}
                className="featured-carousel"
								fade={false}
								slide={true}
              >
                {featuredChunks.map((chunk, index) => (
                  <Carousel.Item key={index}>
                    <Row>
                      {chunk.map((item) => (
                        <Col md={3} sm={6} key={item._id} className="mb-4 d-flex">
                          <Card className="h-100 w-100 animate__animated animate__fadeInUp">
                            <div className="featured-img-container">
															<Card.Img 
																variant="top" 
																src={getImageUrl(item.image)} 
																className="featured-item-img"
																onError={(e) => {
																	e.target.src = 'https://via.placeholder.com/300x180?text=No+Image';
																}}
															/>
                            </div>
                            <Card.Body className="d-flex flex-column">
                              <Card.Title>{item.name}</Card.Title>
                              <div className="mb-2">
                                {[...Array(5)].map((_, i) => (
                                  <FontAwesomeIcon 
                                    icon={faStar} 
                                    key={i} 
                                    className={i < Math.floor(item.rating) ? "text-warning" : "text-secondary"} 
                                  />
                                ))}
                              </div>
                              <div className="mt-auto d-flex justify-content-between align-items-center">
                                <span className="fw-bold">₦{item.price?.toLocaleString()}</span>
                                <Button variant="danger" size="sm" onClick={() => addToCart(item)}>
                                  Add to Cart
                                </Button>
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  </Carousel.Item>
                ))}
              </Carousel>
            ) : (
              // Grid for 4 or fewer items
              <Row>
                {featuredItems.map((item) => (
                  <Col md={3} sm={6} key={item._id} className="mb-4 d-flex">
                    <Card className="h-100 w-100 animate__animated animate__fadeInUp">
                      <div className="featured-img-container">
												<Card.Img 
													variant="top" 
													src={getImageUrl(item.image)} 
													className="featured-item-img"
													onError={(e) => {
														e.target.src = 'https://via.placeholder.com/300x180?text=No+Image';
													}}
												/>
                      </div>
                      <Card.Body className="d-flex flex-column">
                        <Card.Title>{item.name}</Card.Title>
                        <div className="mb-2">
                          {[...Array(5)].map((_, i) => (
                            <FontAwesomeIcon 
                              icon={faStar} 
                              key={i} 
                              className={i < Math.floor(item.rating) ? "text-warning" : "text-secondary"} 
                            />
                          ))}
                        </div>
                        <div className="mt-auto d-flex justify-content-between align-items-center">
                          <span className="fw-bold">₦{item.price?.toLocaleString()}</span>
                          <Button variant="danger" size="sm" onClick={() => addToCart(item)}>
                            Add to Cart
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </>
        )}
      </Container>

			{/* Categories Section */}
			<Container className="my-5 py-4">
				<h2 className="text-center mb-4">Our Categories</h2>
				<Row>
					{categories.map((category, index) => (
						<Col md={3} sm={6} key={index} className="mb-4 d-flex">
							<Card className="h-100 w-100 category-card">
								<div className="category-icon-container text-center py-5">
									{category.icon}
								</div>
								<Card.Body className="text-center bg-light">
									<Card.Title>{category.name}</Card.Title>
									<Button 
										variant="outline-danger" 
										onClick={() => {
											if (category.name === 'Custom Orders') {
												navigate('/custom-order');
											} else {
												navigate(`/menu?category=${category.slug}`);
											}
										}}
									>
										Explore
									</Button>
								</Card.Body>
							</Card>
						</Col>
					))}
				</Row>
			</Container>

			{/* Testimonials Section */}
			<Container className="mb-1 pt-4">
				<h2 className="text-center mb-5">What Our Customers Say</h2>
					
				<Row className="justify-content-center">
					<Col lg={10}>
						<Carousel indicators={false} controls={false} nextLabel="" prevLabel="" interval={7000} pause="hover">
							{testimonials.map((testimonial) => (
								<Carousel.Item key={testimonial.id}>
									<Card className="testimonial-card border-0">
										<Card.Body className="text-center py-4">
											<div className="d-flex justify-content-center mb-3">
												{[...Array(5)].map((_, i) => (
													<FontAwesomeIcon 
														key={i}
														icon={faStar}
														className={i < testimonial.rating ? "text-warning" : "text-secondary"}
													/>
												))}
											</div>
											<Card.Text className="testimonial-text fs-5 fst-italic mb-4">
												{testimonial.comment}
											</Card.Text>
											<div className="d-flex align-items-center justify-content-center">
												<img 
													src={testimonial.image} 
													alt={testimonial.name}
													className="rounded-circle me-3"
													width="60"
													height="60"
												/>
												<div>
													<h5 className="mb-1">{testimonial.name}</h5>
													<small className="text-muted">{testimonial.role}</small>
												</div>
											</div>
										</Card.Body>
									</Card>
								</Carousel.Item>
							))}
						</Carousel>
					</Col>
				</Row>
			</Container>

			{/* Newsletter Section */}
			<Container fluid className="newsletter-section pt-1y pb-5 bg-light">
				<Row className="justify-content-center">
					<Col md={8} lg={6} className="text-center">
						<h2 className="mb-4">Stay Updated with Noureesh Foods</h2>
						<p className="mb-4">Subscribe to our newsletter for exclusive offers, new menu items, and special discounts!</p>

						<Form className="newsletter-form" onSubmit={handleSubmit}>
							{subscribed ? (
								<div className="alert alert-success py-3">
									Thank you for subscribing! 🎉
								</div>
							) : error ? (
								<div className="alert alert-danger py-3">
									{error}
								</div>
							) : (
								<>
									<InputGroup className="mb-3">
										<Form.Control
											type="email"
											placeholder="Your email address"
											aria-label="Email address"
											className="py-3"
											value={email}
											onChange={(e) => {
												setEmail(e.target.value);
												setError(null); // Clear error when typing
											}}
											required
										/>
										<Button variant="danger" className="px-4" type="submit">
											Subscribe
										</Button>
									</InputGroup>
									<Form.Text className="text-muted small">
										We'll never share your email. Unsubscribe anytime.
									</Form.Text>
								</>
							)}
						</Form>
					</Col>
				</Row>
			</Container>

			{/* About Section */}
			<div className="bg-danger text-white py-5 mb-0" style={{ width: '100%' }}>
				<Container>
					<h2>About Noureesh Foods</h2>
					<p>
						We are a family-owned business specializing in homemade cakes, cookies, desserts, and traditional rice dishes.
						Our recipes have been passed down through generations, ensuring authentic flavors in every bite.
					</p>
					<Button as={Link} to="/about" variant="light">Learn More</Button>
				</Container>
			</div>
		</div>
  );
}

export default Home;